/**
 * useFieldDefaultSaver — zapis wartości domyślnej pola modułu systemowego (PUT
 * `/system-modules/:slug/fields/:fieldId` z `defaultValue`), wspólny dla obu list pól:
 * Ustawienia kartoteki → Pola (`SystemModuleFieldsPanel`) i Ustawienia → Pola modułów
 * (`ModuleFieldsTab`).
 *
 * Wartość w trakcie zapisu (`valueOf`) komórka pokazuje od razu, do czasu odświeżenia listy pól;
 * błąd zapisu → komunikat i powrót do wartości z serwera.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

/** Klucz komunikatu błędu zapisu wartości domyślnej (kody trasy pola). */
function saveErrorKey(err: unknown): string {
  const code = isAxiosError(err)
    ? (err.response?.data as { error?: unknown } | undefined)?.error
    : null;
  switch (code) {
    case 'DEFAULT_VALUE_INVALID':
      return 'tenant.systemModuleFields.defaultValue.errors.invalid';
    case 'DEFAULT_VALUE_NOT_ALLOWED':
      return 'tenant.systemModuleFields.defaultValue.errors.notAllowed';
    case 'FORBIDDEN':
      return 'tenant.systemModuleFields.defaultValue.errors.forbidden';
    default:
      return 'tenant.systemModuleFields.defaultValue.errors.saveFailed';
  }
}

interface SaveArgs {
  moduleSlug: string;
  fieldId: string;
  value: string | null;
}

export interface FieldDefaultSaver {
  /** Wartość do pokazania: w trakcie zapisu — zapisywana, inaczej — z serwera. */
  valueOf: (fieldId: string, serverValue: string | null) => string | null;
  save: (moduleSlug: string, fieldId: string, value: string | null) => void;
}

export function useFieldDefaultSaver(): FieldDefaultSaver {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Record<string, string | null>>({});

  const clearPending = (fieldId: string) =>
    setPending((prev) => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });

  const mutation = useMutation({
    mutationFn: async ({ moduleSlug, fieldId, value }: SaveArgs) => {
      await api.put(`/system-modules/${moduleSlug}/fields/${fieldId}`, { defaultValue: value });
    },
    onSuccess: async (_data, { moduleSlug, fieldId }) => {
      toast.show({ variant: 'success', title: t('tenant.systemModuleFields.defaultValue.saved') });
      await Promise.all([
        // Ustawienia kartoteki → Pola (snapshot modułu) i Ustawienia → Pola modułów (lista pól).
        queryClient.refetchQueries({ queryKey: ['system-module', moduleSlug] }),
        queryClient.refetchQueries({ queryKey: ['system-module-fields-list', moduleSlug] }),
        queryClient.invalidateQueries({ queryKey: ['system-module-fields', moduleSlug] }),
      ]);
      clearPending(fieldId);
    },
    onError: (err, { fieldId }) => {
      clearPending(fieldId);
      toast.show({ variant: 'error', title: t(saveErrorKey(err)) });
    },
  });

  return {
    valueOf: (fieldId, serverValue) =>
      fieldId in pending ? (pending[fieldId] ?? null) : serverValue,
    save: (moduleSlug, fieldId, value) => {
      setPending((prev) => ({ ...prev, [fieldId]: value }));
      mutation.mutate({ moduleSlug, fieldId, value });
    },
  };
}
